
import React, { useEffect, useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import toast, { Toaster } from 'react-hot-toast';
import { base_url } from 'Backend/api';
import { useNavigate } from 'react-router-dom';
import { getFcmToken } from 'getFcmToken';
import { registerFcmToken } from 'Backend/registerFcmToken';
import { validatePhoneNumber } from 'utils/validatePhoneNumber';

/** رقم سوري 09xxxxxxxx — يُستخدم لاكتشاف الملء التلقائي الخاطئ في حقل الإيميل */
function looksLikeSyrianPhone(value) {
  return /^09\d{8}$/.test(String(value ?? "").trim());
}

const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setPhoneError('');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /** إذا المتصفح ملأ رقم الهاتف في خانة الإيميل */
  const fixMisplacedPhoneAutofill = () => {
    setFormData((prev) => {
      const emailVal = (prev.email || "").trim();
      const phoneVal = (prev.phone || "").trim();
      if (looksLikeSyrianPhone(emailVal) && !phoneVal) {
        return { ...prev, phone: emailVal, email: "" };
      }
      return prev;
    });
  };

  useEffect(() => {
    const timer = window.setTimeout(fixMisplacedPhoneAutofill, 200);
    return () => window.clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneValidationError = validatePhoneNumber(formData.phone);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      toast.error(phoneValidationError);
      return;
    }

    setLoading(true);

    const body = {
      full_name: formData.fullName,
      email: formData.email,
      phone: formData.phone.trim(),
      password: formData.password,
      password_confirmation: formData.confirmPassword,
      user_type: 'client'
    };

    try {
      const res = await fetch(`${base_url}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (res.ok && data.status) {
        
        localStorage.setItem('user', JSON.stringify(data.data.user));
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('phone', data.data.user.phone);
        localStorage.setItem('slug', data.data.user.slug);
        toast.success(data.message || 'تم التسجيل بنجاح');
        const token = data.data.token;
       
        await setActiveLocation(token);
        navigate('/restaurant-home-page');
        const fcmToken = await getFcmToken();

        if (fcmToken) {
          await registerFcmToken(fcmToken, token);
        }
      } else {
        toast.error(data.message || 'حدث خطأ، حاول مرة أخرى');
      }
    } catch (error) {
      toast.error('حدث خطأ في الاتصال بالسيرفر');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("المتصفح لا يدعم الموقع");
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => reject(error)
        );
      }
    });
  };

  const getAddressFromCoords = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();

      return {
        full_address: data.display_name || "",
        city: data.address?.city || data.address?.town || "",
        district: data.address?.suburb || "",
        street: data.address?.road || "",
      };
    } catch (e) {
      return {
        full_address: "",
        city: "",
        district: "",
        street: "",
      };
    }
  };

  const setActiveLocation = async (token) => {
    try {
      const location = await getCurrentLocation();
      const address = await getAddressFromCoords(location.lat, location.lng);

      const body = {
        map_location: `${location.lat},${location.lng}`,
        full_address: address.full_address,
        city: address.city,
        district: address.district,
        street: address.street,
      };

      await fetch(`${base_url}/api/addresses/set-active`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(body),
      });

    } catch (err) {
      console.log("Location error:", err);
    }
  };

  return (
    <>
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          style: {
            fontFamily: 'Cairo, sans-serif',
            fontWeight: '500',
            fontSize: '14px',
            textAlign: 'right',
            direction: 'rtl',
          },
          success: { duration: 5000 },
          error: { duration: 5000 },
        }}
      />

      <form
        onSubmit={handleSubmit}
        autoComplete="on"
        className="space-y-4 md:space-y-5 font-[cairo] px-3 md:px-4"
      >
        {/* Full Name */}
        <div className="space-y-1 md:space-y-2">
          <label htmlFor="signup-fullName" className="block text-sm md:text-base font-medium text-right">
            <span>الاسم الكامل</span>
            <span className="mr-1 text-[#F93209]">*</span>
          </label>
          <input
            id="signup-fullName"
            type="text"
            name="fullName"
            autoComplete="name"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="أدخل اسمك الكامل"
            className="w-full px-3 md:px-4 py-2.5 md:py-3  rounded-[8px] text-right border border-[#E5E7EB] focus:border-red-500 focus:outline-none focus:ring-0 text-sm md:text-base"
          />
        </div>

        {/* Phone — قبل الإيميل لتقليل الملء التلقائي الخاطئ */}
        <div className="space-y-1 md:space-y-2">
          <label htmlFor="signup-phone" className="block text-sm md:text-base font-medium text-right">
            <span>رقم الهاتف</span>
            <span className="mr-1 text-[#F93209]">*</span>
          </label>
          <input
            id="signup-phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            value={formData.phone}
            onChange={handleChange}
            onBlur={() => setPhoneError(validatePhoneNumber(formData.phone) || '')}
            placeholder="09xxxxxxxx"
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-[8px] text-right border focus:border-red-500 focus:outline-none focus:ring-0 text-sm md:text-base ${
              phoneError ? 'border-red-500' : 'border-[#E5E7EB]'
            }`}
          />
          {phoneError && (
            <p className="text-right text-xs text-red-500">{phoneError}</p>
          )}
        </div>

        {/* Email */}
        <div className="space-y-1 md:space-y-2">
          <label htmlFor="signup-email" className="block text-sm md:text-base font-medium text-right">
            البريد الإلكتروني
          </label>
          <input
            id="signup-email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={fixMisplacedPhoneAutofill}
            placeholder="example@email.com"
            className="w-full px-3 md:px-4 py-2.5 md:py-3  rounded-[8px] text-right border border-[#E5E7EB] focus:border-red-500 focus:outline-none focus:ring-0 text-sm md:text-base"
          />
        </div>

        {/* Password */}
        <div className="relative space-y-1 md:space-y-2">
          <label htmlFor="signup-password" className="block text-sm md:text-base font-medium text-right">
            <span>كلمة المرور</span>
            <span className="mr-1 text-[#F93209]">*</span>
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              placeholder="أدخل كلمة المرور"
              className="w-full px-3 md:px-4 py-2.5 md:py-3  rounded-[8px] text-right border border-[#E5E7EB] focus:border-red-500 focus:outline-none focus:ring-0 text-sm md:text-base"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2"
            >
              <Icon color='#9CA3AF' name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="relative space-y-1 md:space-y-2">
          <label htmlFor="signup-confirmPassword" className="block text-sm md:text-base font-medium text-right">
            <span>تأكيد كلمة المرور</span>
            <span className="mr-1 text-[#F93209]">*</span>
          </label>
          <div className="relative">
            <input
              id="signup-confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="أعد إدخال كلمة المرور"
              className="w-full px-3 md:px-4 py-2.5 md:py-3 border border-[#E5E7EB] rounded-[8px] text-right focus:border-red-500 focus:outline-none focus:ring-0 text-sm md:text-base"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2"
            >
              <Icon color='#9CA3AF' name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
        </div>

        <Button
          type="submit"
          size="lg"
          fullWidth
          className="mt-4 md:mt-6 font-medium text-sm md:text-base rounded-[8px]"
          disabled={loading}
        >
          {loading ? 'جاري التسجيل...' : 'إنشاء حساب'}
        </Button>
      </form>
    </>
  );
};

export default SignupForm;